import type { ThunkActionCommon, ThunkDispatchCommon } from "single-spa/store/types";

import { systemMessageService } from "../../services/system-message/SystemMessageService";
import type { SystemMessageType } from "../../types/systemMessages";
import type { SystemMessageContext } from "../../types/systemMessages";
import type { AiThreadMessage, SendMessageResult } from "../../types/thread";

import { addAgentMessage, addHumanMessage, clearMessages, setUserMessageData } from "./action-creators";
import type { UserMessageData } from "./types";

/**
 * Generic system message effect that can be used by any module
 */
export const setSystemMessage =
    <Payload extends Omit<UserMessageData, "message"> = Omit<UserMessageData, "message">>(
        type: SystemMessageType,
        context: SystemMessageContext,
        payload?: Payload | null,
    ) =>
    (dispatch: ThunkDispatchCommon): SendMessageResult => {
        const content = systemMessageService.getMessageContent(type, context);
        const shouldReset = systemMessageService.shouldResetMessages(type);

        if (shouldReset) {
            dispatch(clearMessages());
        }

        const messagePayload: UserMessageData = {
            message: content,
            ...(payload ?? {}),
        };

        dispatch(setUserMessageData(messagePayload));

        return { success: true };
    };

/**
 * Generic user message effect
 */
export const setUserMessage =
    <Payload extends UserMessageData = UserMessageData>(
        { message, ...rest }: Partial<Payload>,
        shouldResetMessages: boolean = false,
    ): ThunkActionCommon<SendMessageResult> =>
    (dispatch) => {
        const trimmedMessage = message?.trim();

        if (!trimmedMessage) {
            return { success: false };
        }

        if (shouldResetMessages) {
            dispatch(clearMessages());
        }

        const messagePayload: UserMessageData = {
            message: trimmedMessage,
            ...rest,
        };

        dispatch(setUserMessageData(messagePayload));
        dispatch(addHumanMessage(trimmedMessage));

        return { success: true };
    };

/**
 * Generic agent message effect
 */
export const setAgentMessage =
    (payload: Omit<AiThreadMessage, "type">, shouldResetMessages: boolean = false) =>
    (dispatch: ThunkDispatchCommon): SendMessageResult => {
        if (shouldResetMessages) {
            dispatch(clearMessages());
        }

        dispatch(addAgentMessage(payload));

        return { success: true };
    };
